/**
 * Helpers for the "typed for display, generic for placement" entity pickers.
 *
 * A selector fetches its typed toolbox action with entity_format="both": the
 * response carries the typed entities in `data` (for the rich row UI) and their
 * index-aligned generic projections in `data_generic` (what actually lands on
 * the canvas). These helpers turn `data_generic` into an id→generic lookup so
 * each EntityDragRow can be handed the generic for the typed entity it renders.
 *
 * The generic's id equals the typed entity's id (the server's ToGeneric copies
 * `id` through), so the typed list and the lookup share keys.
 */
import type { GenericEntity } from "@cohesive/api-entities";

/** Normalize a response's `data_generic` (single | array | undefined) to an array. */
export function toGenericArray(
  dataGeneric: GenericEntity | GenericEntity[] | undefined,
): GenericEntity[] {
  if (!dataGeneric) return [];
  return Array.isArray(dataGeneric) ? dataGeneric : [dataGeneric];
}

/** Build an id→generic lookup from the generic projections of one or more pages. */
export function buildGenericById(
  generics: GenericEntity[],
): Map<string, GenericEntity> {
  const byId = new Map<string, GenericEntity>();
  for (const g of generics) {
    const id = g?.data?.id;
    if (id) byId.set(id, g);
  }
  return byId;
}
